import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Landmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getIntradayFeedStatus, refreshIntradayFeed } from "@/lib/api";
import { BankFeedSettings } from "./BankFeedSettings";

const hhmm = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" }); } catch { return null; }
};

// Dean, Oct 2026: just the action — the feed status lives in the tooltip; the gear opens schedule + status.
export function BankFeedChip({ onRefreshed }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setStatus(await getIntradayFeedStatus()); } catch { setStatus(null); }
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await refreshIntradayFeed();
      const fresh = (r.run?.accounts || []).reduce((n, a) => n + (a.new || 0), 0);
      if (r.run?.errors?.length) toast.warning(`Bank feed pulled with warnings: ${r.run.errors[0]}`);
      else toast.success(`Bank feed refreshed — ${r.run?.rows_today ?? 0} credits today (${fresh} new). ${r.budget?.manual_left_today} manual pulls left today.`);
      await load();
      onRefreshed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bank feed refresh failed");
    } finally { setBusy(false); }
  };

  if (!status) return null;
  const live = status.production && status.settings?.enabled;
  const left = status.budget?.manual_left_today ?? 0;
  const disabled = busy || !status.production || left <= 0;
  const last = status.last_run;
  const sched = status.schedule;
  const schedLine = sched ? (sched.state === "on" ? `Scheduled pulls running — ${sched.reason}.`
    : sched.state === "outside_window" ? `Scheduled pulls paused — ${sched.reason}.`
    : sched.state === "off" ? "Scheduled pulls are OFF — switch them on from the gear."
    : sched.state === "scheduler_down" ? `⚠ Scheduled pulls NOT running — ${sched.reason}.`
    : "") : "";
  const tip = !status.production
    ? "The portal polls NatWest / Barclays through GoCardless on a schedule on production only. This preview reads the Airtable tab."
    : left <= 0 ? `Manual refresh limit reached (${status.budget.manual_pulls_per_day}/day). ${schedLine}`
    : `Pull today's credits now (${left} of ${status.budget.manual_pulls_per_day} manual pulls left). ${last?.finished_at ? `Last pull ${hhmm(last.finished_at)} · ` : ""}${status.rows_today} credits today, ${status.pending_today} pending. ${schedLine}`;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <span data-testid="bank-feed-chip">
              <Button variant="outline" size="sm" className="h-8" onClick={refresh} disabled={disabled} data-testid="bank-feed-refresh-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Landmark className={`w-4 h-4 mr-2 ${sched?.state === "scheduler_down" ? "text-red-600" : live ? "text-emerald-600" : ""}`} />}
                Refresh bank feed
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs text-xs" data-testid="bank-feed-status">{tip}</TooltipContent>
        </Tooltip>
        <BankFeedSettings status={status} onSaved={load} />
      </div>
    </TooltipProvider>
  );
}
